import { Button, PageHeader, TabBar } from '@ksyos/design-system';
import { Link as ReactRouterLink, useNavigate } from '@ksyos/router';
import { useState } from 'react';
import { ManagerAuthorization, ToastType } from '@common/enums';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { assertHasAuthorization, hasOneAuthorization, showToast } from '@frontend/utils';
import { getSelectedLanguage } from '../../../translate';
import { DeviceAccessoriesList } from '../deviceAccessories/DeviceAccessoriesList';
import { AddDeviceModal } from '../devices/AddDeviceModal';
import { DevicesList } from '../devices/DevicesList';

export enum DevicesAndAccessoriesTab {
    accessories = 'accessories',
    devices = 'devices',
}

export function DevicesAndAccessories(props: Readonly<{ tab: DevicesAndAccessoriesTab }>): JSX.Element {
    assertHasAuthorization(ManagerAuthorization.devicesRead);
    const navigate = useNavigate();
    const [selectedTab, setSelectedTab] = useState<DevicesAndAccessoriesTab>(props.tab);
    const [showAddDeviceModal, setShowAddDeviceModal] = useState<boolean>(false);
    const [shouldBeReloaded, setShouldBeReloaded] = useState<boolean>(false);
    const isAddEditAllowed = hasOneAuthorization(ManagerAuthorization.devicesAddEdit);
    const translate = getSelectedLanguage();

    return (
        <PageTemplate>
            {showAddDeviceModal && (
                <AddDeviceModal
                    onClose={(isSuccessfullySaved) => onCloseAddDeviceModal(isSuccessfullySaved)}
                    title={translate.devices.addDeviceModalTitle}
                />
            )}
            <PageHeader
                action={renderActionButtonByTab()}
                title={translate.devices.devices}
            />
            <TabBar>
                <TabBar.Button
                    isSelected={selectedTab === DevicesAndAccessoriesTab.devices}
                    key="devices"
                    onClick={() => {
                        setSelectedTab(DevicesAndAccessoriesTab.devices);
                        navigate('/devices');
                    }}
                >
                    {translate.devices.devices}
                </TabBar.Button>
                <TabBar.Button
                    isSelected={selectedTab === DevicesAndAccessoriesTab.accessories}
                    key="accessories"
                    onClick={() => {
                        navigate('/devices/accessories');
                        setSelectedTab(DevicesAndAccessoriesTab.accessories);
                    }}
                >
                    {translate.deviceAccessories.accessories}
                </TabBar.Button>
            </TabBar>

            {selectedTab === DevicesAndAccessoriesTab.devices && (
                <DevicesList
                    setShouldBeReloaded={setShouldBeReloaded}
                    shouldBeReloaded={shouldBeReloaded}
                />
            )}
            {selectedTab === DevicesAndAccessoriesTab.accessories && <DeviceAccessoriesList />}
        </PageTemplate>
    );

    function renderActionButtonByTab() {
        if (!isAddEditAllowed) {
            return null;
        }

        if (selectedTab === DevicesAndAccessoriesTab.devices) {
            return (
                <Button
                    iconLeft="add"
                    key="addDevice"
                    onClick={() => setShowAddDeviceModal(true)}
                >
                    {translate.devices.add}
                </Button>
            );
        } else {
            return (
                <Button
                    as={ReactRouterLink}
                    iconLeft="add"
                    key="addDeviceAccessory"
                    to="/devices/accessories/add"
                >
                    {translate.deviceAccessories.add}
                </Button>
            );
        }
    }

    function onCloseAddDeviceModal(isSuccessfullySaved: boolean) {
        setShowAddDeviceModal(false);
        setShouldBeReloaded(isSuccessfullySaved);
        if (isSuccessfullySaved) {
            showToast(ToastType.success, translate.devices.addDeviceSuccessMessage);
        }
    }
}
